// The scope picker of one assignment — BR-37. Where a role applies: every client, or chosen
// clients, each optionally narrowed to regions or to offices.
//
// It offers only what the caller may grant (`utils/cierreAcceso.js`): "Todos los clientes" only to
// someone who could grant the role on every client, only the clients every limit of the role
// reaches, and of each client only the whole, the regions and the offices inside those limits. So
// a Supervisor of Davivienda never sees another client here, and a Supervisor of one region never
// sees "Todo el cliente". The backend still decides; this is what keeps the screen from offering
// what it will refuse.
//
// Cliente (`clientBound`) names exactly one client, so its clients are a radio list and choosing a
// second one replaces the first.
//
// Native inputs rather than the package's Radix checkboxes: a list of a hundred offices is a list
// of checkboxes, and the package's need a portal-free jsdom shim to test (see AtenderAlerta.test).
// Offices are loaded per client, on demand, by the dialog that owns the cache (`cargarOficinas`).
import { useEffect, useMemo, useState } from "react";

import {
  clientesOtorgables,
  mapaDeRegiones,
  opcionesDeCliente,
  permite,
  todosOtorgable,
} from "../../../utils/cierreAcceso";

const CHIP_BASE =
  "inline-flex h-7 items-center rounded-[6px] border px-2.5 text-[12px] font-medium transition disabled:cursor-not-allowed disabled:opacity-45";
const CHIP_ACTIVO = "border-transparent bg-primary text-primary-foreground";
const CHIP_INACTIVO = "border-brinks-border bg-card text-brinks-foreground hover:bg-brinks-surface-hover";

const MAX_SIN_FILTRO = 12;

const modoInicial = (entero, opciones) => {
  if (entero) return "entero";
  if (opciones && opciones.regiones.length === 0 && opciones.oficinas.length > 0) return "oficinas";
  return "regiones";
};

function ListaMarcable({ id, titulo, elementos, marcados, onCambiar, etiquetaDe, claveDe }) {
  const [filtro, setFiltro] = useState("");
  const texto = filtro.trim().toLowerCase();
  const visibles = texto
    ? elementos.filter((e) => etiquetaDe(e).toLowerCase().includes(texto))
    : elementos;

  const alternar = (clave) =>
    onCambiar(marcados.includes(clave) ? marcados.filter((m) => m !== clave) : [...marcados, clave]);

  return (
    <div className="space-y-1.5">
      {elementos.length > MAX_SIN_FILTRO ? (
        <input
          type="search"
          aria-label={`Buscar ${titulo}`}
          placeholder={`Buscar ${titulo}…`}
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          className="h-8 w-full rounded-[6px] border border-brinks-border bg-card px-2.5 text-[12.5px] text-brinks-foreground"
        />
      ) : null}
      <div role="group" aria-label={titulo} className="max-h-48 space-y-0.5 overflow-y-auto rounded-[8px] border border-brinks-border bg-card p-1.5">
        {visibles.length === 0 ? (
          <p className="px-1 py-1 text-[12px] text-brinks-muted-foreground">Nada coincide con la búsqueda.</p>
        ) : (
          visibles.map((elemento) => {
            const clave = claveDe(elemento);
            const inputId = `${id}-${clave}`;
            return (
              <label
                key={clave}
                htmlFor={inputId}
                className="flex cursor-pointer items-center gap-2 rounded-[6px] px-1.5 py-1 text-[12.5px] text-brinks-foreground hover:bg-brinks-surface-hover"
              >
                <input
                  id={inputId}
                  type="checkbox"
                  checked={marcados.includes(clave)}
                  onChange={() => alternar(clave)}
                />
                <span>{etiquetaDe(elemento)}</span>
              </label>
            );
          })
        )}
      </div>
      <p className="text-[11.5px] text-brinks-muted-foreground">
        {marcados.length} de {elementos.length} seleccionadas
      </p>
    </div>
  );
}

function DetalleDeCliente({ idBase, entrada, nombre, opciones, enteroPermitido, onCambiar }) {
  const modos = [
    { modo: "entero", etiqueta: "Todo el cliente", permitido: enteroPermitido },
    { modo: "regiones", etiqueta: "Regiones", permitido: Boolean(opciones && opciones.regiones.length > 0) },
    { modo: "oficinas", etiqueta: "Oficinas", permitido: Boolean(opciones && opciones.oficinas.length > 0) },
  ];

  return (
    <div className="ml-6 space-y-2 border-l border-brinks-border pl-3">
      <div role="radiogroup" aria-label={`Alcance en ${nombre}`} className="flex flex-wrap gap-1.5">
        {modos.map((m) => (
          <button
            key={m.modo}
            type="button"
            role="radio"
            aria-checked={entrada.modo === m.modo}
            disabled={!m.permitido && entrada.modo !== m.modo}
            onClick={() => onCambiar({ ...entrada, modo: m.modo })}
            className={`${CHIP_BASE} ${entrada.modo === m.modo ? CHIP_ACTIVO : CHIP_INACTIVO}`}
          >
            {m.etiqueta}
          </button>
        ))}
      </div>

      {entrada.modo === "entero" ? (
        <p className="text-[12px] text-brinks-muted-foreground">
          Todas las oficinas de {nombre}, también las que se registren después.
        </p>
      ) : opciones === null ? (
        <p className="text-[12px] text-brinks-muted-foreground">Cargando oficinas…</p>
      ) : entrada.modo === "regiones" ? (
        <>
          <ListaMarcable
            id={`${idBase}-${entrada.code}-regiones`}
            titulo={`regiones de ${nombre}`}
            elementos={opciones.regiones}
            marcados={entrada.regions}
            onCambiar={(regions) => onCambiar({ ...entrada, regions })}
            etiquetaDe={(r) => r}
            claveDe={(r) => r}
          />
          <p className="text-[11.5px] text-brinks-muted-foreground">
            Una región incluye las oficinas que se registren en ella después.
          </p>
        </>
      ) : (
        <ListaMarcable
          id={`${idBase}-${entrada.code}-oficinas`}
          titulo={`oficinas de ${nombre}`}
          elementos={opciones.oficinas}
          marcados={entrada.offices}
          onCambiar={(offices) => onCambiar({ ...entrada, offices })}
          etiquetaDe={(o) => [o.officeCode, o.name, o.region].filter(Boolean).join(" · ")}
          claveDe={(o) => o.officeCode}
        />
      )}
    </div>
  );
}

export function AccesoSelectorAlcance({
  idBase,
  me,
  rol,
  clientes,
  oficinasPorCliente,
  cargarOficinas,
  valor,
  onChange,
}) {
  const todos = todosOtorgable(me, rol);
  const visibles = useMemo(() => clientesOtorgables(me, rol, clientes), [me, rol, clientes]);
  const regionDe = useMemo(() => mapaDeRegiones(oficinasPorCliente), [oficinasPorCliente]);
  const unSolo = Boolean(rol && rol.clientBound);

  const nombreDe = (code) => (clientes || []).find((c) => c.code === code)?.name || code;
  const enteroPermitido = (code) => permite(me, rol, { tipo: "cliente", cliente: code });
  const opcionesDe = (code) =>
    oficinasPorCliente[code] === undefined
      ? null
      : opcionesDeCliente(me, rol, code, oficinasPorCliente[code], regionDe);

  // Offices are needed to offer a client's regions and offices: for every client already chosen,
  // and for every client the caller cannot grant whole — the default mode depends on them.
  const elegidos = valor.all ? [] : valor.clients.map((c) => c.code);
  const necesarios = [
    ...elegidos,
    ...visibles.filter((c) => !enteroPermitido(c.code)).map((c) => c.code),
  ];
  const claveNecesarios = [...new Set(necesarios)].sort().join(",");
  useEffect(() => {
    if (!claveNecesarios) return;
    claveNecesarios.split(",").forEach((code) => cargarOficinas(code));
  }, [claveNecesarios, cargarOficinas]);

  if (!rol) {
    return <p className="text-[12.5px] text-brinks-muted-foreground">Elige primero un rol.</p>;
  }

  const elegir = (code) => {
    const entrada = { code, modo: modoInicial(enteroPermitido(code), opcionesDe(code)), regions: [], offices: [] };
    if (unSolo) {
      onChange({ all: false, clients: [entrada] });
      return;
    }
    onChange({ all: false, clients: [...valor.clients, entrada] });
  };
  const quitar = (code) => onChange({ all: false, clients: valor.clients.filter((c) => c.code !== code) });
  const cambiarEntrada = (entrada) =>
    onChange({ all: false, clients: valor.clients.map((c) => (c.code === entrada.code ? entrada : c)) });

  // A client chosen earlier that the current role cannot reach stays listed, so it can be removed;
  // the dialog's problems list says why it cannot be saved.
  const fueraDeAlcance = valor.all
    ? []
    : valor.clients.filter((c) => !visibles.some((v) => v.code === c.code)).map((c) => ({ code: c.code, name: nombreDe(c.code) }));
  const listados = [...visibles, ...fueraDeAlcance];

  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-[12.5px] font-semibold text-brinks-foreground">Alcance</legend>

      <div role="radiogroup" aria-label="Alcance" className="space-y-1">
        <label className="flex items-start gap-2 text-[12.5px] text-brinks-foreground">
          <input
            type="radio"
            name={`${idBase}-alcance`}
            checked={valor.all}
            disabled={!todos}
            onChange={() => onChange({ all: true, clients: [] })}
          />
          <span>
            Todos los clientes
            <span className="block text-[11.5px] text-brinks-muted-foreground">
              {unSolo
                ? "Este rol debe limitarse a un solo cliente."
                : todos
                ? "Incluye los clientes que se registren después."
                : "No puedes otorgar este rol sobre todos los clientes."}
            </span>
          </span>
        </label>
        <label className="flex items-center gap-2 text-[12.5px] text-brinks-foreground">
          <input
            type="radio"
            name={`${idBase}-alcance`}
            checked={!valor.all}
            disabled={visibles.length === 0 && !valor.all}
            onChange={() => onChange({ all: false, clients: [] })}
          />
          <span>{unSolo ? "Un cliente" : "Clientes elegidos"}</span>
        </label>
      </div>

      {valor.all ? null : listados.length === 0 ? (
        <p className="text-[12px] text-brinks-muted-foreground">
          No hay clientes sobre los que puedas otorgar este rol.
        </p>
      ) : (
        <div className="space-y-1.5">
          {listados.map((cliente) => {
            const entrada = valor.clients.find((c) => c.code === cliente.code);
            const inputId = `${idBase}-cliente-${cliente.code}`;
            const fuera = fueraDeAlcance.some((f) => f.code === cliente.code);
            return (
              <div key={cliente.code} className="space-y-1.5">
                <label htmlFor={inputId} className="flex items-center gap-2 text-[12.5px] font-medium text-brinks-foreground">
                  <input
                    id={inputId}
                    type={unSolo ? "radio" : "checkbox"}
                    name={unSolo ? `${idBase}-cliente` : undefined}
                    checked={Boolean(entrada)}
                    onChange={() => (entrada && !unSolo ? quitar(cliente.code) : elegir(cliente.code))}
                  />
                  <span>{cliente.name || cliente.code}</span>
                  {fuera ? <span className="text-[11.5px] font-normal text-brinks-danger">fuera de tu alcance</span> : null}
                </label>
                {entrada && !fuera ? (
                  <DetalleDeCliente
                    idBase={idBase}
                    entrada={entrada}
                    nombre={cliente.name || cliente.code}
                    opciones={opcionesDe(cliente.code)}
                    enteroPermitido={enteroPermitido(cliente.code)}
                    onCambiar={cambiarEntrada}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}
